/**
 * Classic interlocking jigsaw edge geometry (shared tabs between neighbors).
 * Square grid cells with semicircular tabs — matches JigsawPuzzle / try.jsx reference.
 */

/** Deterministic PRNG for stable tab directions per grid. */
function mulberry32(seed) {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function hashSeed(rows, cols, salt = 1) {
  return ((rows * 73856093) ^ (cols * 19349663) ^ (salt * 83492791)) >>> 0;
}

/**
 * Tab sign on an edge: +1 = tab pointing "positive" direction, -1 = opposite.
 */
export function buildEdgeTabs(rows, cols, seed) {
  const rnd = mulberry32(seed ?? hashSeed(rows, cols));
  const hEdges = Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => 0)
  );
  const vEdges = Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => 0)
  );

  for (let r = 1; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      hEdges[r][c] = rnd() < 0.5 ? 1 : -1;
    }
  }
  for (let r = 0; r < rows; r += 1) {
    for (let c = 1; c < cols; c += 1) {
      vEdges[r][c] = rnd() < 0.5 ? 1 : -1;
    }
  }
  return { hEdges, vEdges };
}

/** +1 = outward tab, -1 = inward blank, 0 = flat border edge. */
export function pieceSides(row, col, rows, cols, hEdges, vEdges) {
  return {
    top: row === 0 ? 0 : -hEdges[row][col],
    right: col === cols - 1 ? 0 : vEdges[row][col + 1],
    bottom: row === rows - 1 ? 0 : hEdges[row + 1][col],
    left: col === 0 ? 0 : -vEdges[row][col],
  };
}

/**
 * Semicircular jigsaw tab on a unit edge (0,0)→(1,0).
 * Keeps the piece body square; tabs are small rounded knobs on each side.
 */
function unitEdgeWithTab(sign, tabSize = 0.16) {
  if (!sign) {
    return "L 1 0";
  }
  const s = sign > 0 ? 1 : -1;
  const halfW = 0.17;
  const d = tabSize;
  return [
    `L ${0.5 - halfW} 0`,
    `C ${0.5 - halfW} ${s * d * 0.28}, ${0.5 - halfW * 0.35} ${s * d}, ${0.5} ${s * d}`,
    `C ${0.5 + halfW * 0.35} ${s * d}, ${0.5 + halfW} ${s * d * 0.28}, ${0.5 + halfW} 0`,
    `L 1 0`,
  ].join(" ");
}

/** Map unit-edge path onto segment (x0,y0)→(x1,y1). */
function mapUnitPath(unitCmds, x0, y0, x1, y1) {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const nx = -dy;
  const ny = dx;

  return unitCmds.replace(
    /([MLC])\s*([-\d.eE]+)[\s,]+([-\d.eE]+)(?:[\s,]+([-\d.eE]+)[\s,]+([-\d.eE]+)[\s,]+([-\d.eE]+)[\s,]+([-\d.eE]+))?/g,
    (_, cmd, ...nums) => {
      const pairs = [];
      if (cmd === "C") {
        pairs.push(
          [+nums[0], +nums[1]],
          [+nums[2], +nums[3]],
          [+nums[4], +nums[5]]
        );
      } else {
        pairs.push([+nums[0], +nums[1]]);
      }
      const mapped = pairs
        .map(([u, v]) => {
          const x = x0 + u * dx + v * nx;
          const y = y0 + u * dy + v * ny;
          return `${x.toFixed(3)} ${y.toFixed(3)}`;
        })
        .join(" ");
      return `${cmd} ${mapped}`;
    }
  );
}

/** SVG path walking clockwise: top → right → bottom → left. */
export function buildPiecePath(
  row,
  col,
  rows,
  cols,
  boardW,
  boardH,
  sides,
  tabSize = 0.16
) {
  const cw = boardW / cols;
  const ch = boardH / rows;
  const x0 = col * cw;
  const y0 = row * ch;
  const x1 = x0 + cw;
  const y1 = y0 + ch;

  const top = mapUnitPath(unitEdgeWithTab(-sides.top, tabSize), x0, y0, x1, y0);
  const right = mapUnitPath(
    unitEdgeWithTab(-sides.right, tabSize),
    x1,
    y0,
    x1,
    y1
  );
  const bottom = mapUnitPath(
    unitEdgeWithTab(-sides.bottom, tabSize),
    x1,
    y1,
    x0,
    y1
  );
  const left = mapUnitPath(unitEdgeWithTab(-sides.left, tabSize), x0, y1, x0, y0);

  return `M ${x0.toFixed(3)} ${y0.toFixed(3)} ${top} ${right} ${bottom} ${left} Z`;
}

export function buildInterlockPieces(rows, cols, boardW, boardH, options = {}) {
  const { seed, tabSize = 0.16 } = options;
  const { hEdges, vEdges } = buildEdgeTabs(rows, cols, seed);
  const pieces = [];

  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const index = row * cols + col;
      const sides = pieceSides(row, col, rows, cols, hEdges, vEdges);
      const path = buildPiecePath(
        row,
        col,
        rows,
        cols,
        boardW,
        boardH,
        sides,
        tabSize
      );
      pieces.push({
        id: `piece-${index}`,
        originalIndex: index,
        row,
        col,
        sides,
        path,
      });
    }
  }

  return pieces;
}

/** Extra padding (fraction of cell) so tabs aren't clipped. */
export function tabPadFraction(tabSize = 0.16) {
  return tabSize * 1.05;
}
